import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../../utils/core/feature-flags-manager';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../hooks/use-subscribe';
import { DroneLoader } from '../drone-loader/drone-loader';
import { FullPageBox } from '../full-page-box/full-page-box';
import { SkyViewProgressBar } from '../skyview-new/asset-menu/components/skyview-progress-bar';
import { Stack } from '../stack/stack';

const SkyViewLoader = () => {
  const { t } = useTranslation('components');
  const hasError = useSubscribe(SubscriptionTopic.SkyviewLoadingProgressError) !== undefined;
  const loadingProgress = useSubscribe(SubscriptionTopic.SkyviewLoadingProgress);

  if (!FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader')) {
    return (
      <LoaderWrapper
        alignItems="center"
        data-testid="skyview-loader"
        direction="column"
        justifyContent="center"
      >
        <FullPageBox width="28rem">
          {hasError ? (
            <ErrorMessage direction="column" spacing={1.5}>
              <span>
                <i className="fas fa-exclamation-circle" />
                {t('skyviewLoader.error.title', { ns: 'components' })}
              </span>
              <span>
                {t('skyviewLoader.error.descriptionLine1', { ns: 'components' })}
                <br />
                {t('skyviewLoader.error.descriptionLine2', { ns: 'components' })}
              </span>
            </ErrorMessage>
          ) : (
            <SkyViewProgressBar />
          )}
        </FullPageBox>
      </LoaderWrapper>
    );
  }

  if (hasError) {
    return (
      <ErrorWrapper
        alignItems="center"
        data-testid="skyview-loader"
        direction="column"
        justifyContent="center"
      >
        <FullPageBox width="28rem">
          <ErrorMessage direction="column" spacing={1.5}>
            <span>
              <i className="fas fa-exclamation-circle" />
              {t('skyviewLoader.error.title', { ns: 'components' })}
            </span>
            <span>
              {t('skyviewLoader.error.descriptionLine1', { ns: 'components' })}
              <br />
              {t('skyviewLoader.error.descriptionLine2', { ns: 'components' })}
            </span>
          </ErrorMessage>
        </FullPageBox>
      </ErrorWrapper>
    );
  }

  const loadingMessage =
    loadingProgress?.message ?? t('skyviewLoader.loadingText', { ns: 'components' });

  return (
    <DroneLoader
      data-testid="skyview-loader"
      droneSize="100px"
      message={loadingMessage}
      mode="page-content-with-background"
    />
  );
};

const LoaderWrapper = styled(Stack)`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #ccddf0;
`;

const ErrorWrapper = styled(Stack)`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #f4f6f8;
`;

const ErrorMessage = styled(Stack)`
  font-size: 12pt;
  text-align: left;

  span:first-of-type {
    display: block;
    color: #a00;

    i {
      margin-right: 10px;
    }
  }

  span:last-of-type {
    display: block;
    font-style: italic;
    line-height: 1.3em;
    font-size: 0.95em;
  }
`;

export { SkyViewLoader };
